import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, RefreshCw, Tag, Pencil } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getAllPlans,
  createPlan,
  updatePlan,
  deletePlan,
} from "../../services/api";
import { activeCurrencyCode, activeCurrencySymbol } from "../../utils/currency";

interface Plan {
  _id: string;
  name: string;
  durationMonths: number;
  price: number; // cents
  maxLocations?: number;
  isActive: boolean;
}

// Plans are charged in the platform currency set by master admin.
const formatPrice = (minor: number) => `${activeCurrencySymbol()}${(minor / 100).toFixed(2)}`;

const PlansManagement: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  // Plan being edited; null = adding a new plan.
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    durationMonths: "1",
    price: "",
    maxLocations: "1",
  });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await getAllPlans();
      setPlans(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({ name: "", durationMonths: "1", price: "", maxLocations: "1" });
  };

  const openEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      durationMonths: String(plan.durationMonths),
      price: (plan.price / 100).toFixed(2),
      maxLocations: String(plan.maxLocations || 1),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.price.trim()) {
      toast.error("Name and price are required");
      return;
    }

    setFormLoading(true);
    const data = {
      name: formData.name.trim(),
      durationMonths: Math.max(1, Math.floor(Number(formData.durationMonths) || 1)),
      price: Math.round(Number(formData.price) * 100),
      maxLocations: Math.max(1, Math.floor(Number(formData.maxLocations) || 1)),
    };
    try {
      if (editingPlan) {
        await updatePlan(editingPlan._id, data);
        toast.success("Plan updated");
      } else {
        await createPlan(data);
        toast.success("Plan created successfully!");
      }
      resetForm();
      setShowModal(false);
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Failed to ${editingPlan ? "update" : "create"} plan`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (plan: Plan) => {
    try {
      await updatePlan(plan._id, { isActive: !plan.isActive });
      toast.success(plan.isActive ? "Plan deactivated" : "Plan activated");
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to update plan");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await deletePlan(id);
      toast.success("Plan deleted successfully!");
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="relative mx-auto flex h-screen max-w-7xl flex-col overflow-y-auto no-scrollbar p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Plans</h1>
          <div className="flex gap-2">
            <button
              onClick={fetchPlans}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white transition hover:bg-blue-700"
              title="Refresh Plans"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
            >
              <Plus size={18} />
              Add Plan
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1">
        {plans.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No plans yet</p>
              <p className="mt-1 text-sm">Add your first plan to get started</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan._id}
                className="rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={20} className="text-blue-600" />
                    <h3 className="mb-0 text-xl font-bold text-gray-800">
                      {plan.name}
                    </h3>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${
                      plan.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {plan.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice(plan.price)}
                </p>
                <p className="mb-4 text-sm text-gray-500">
                  every {plan.durationMonths} month
                  {plan.durationMonths > 1 ? "s" : ""}
                </p>
                <div className="mb-4 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-gray-700">
                    Up to <b>{plan.maxLocations || 1}</b> location{(plan.maxLocations || 1) > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(plan)}
                    className="flex flex-1 items-center justify-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white transition hover:bg-blue-700"
                  >
                    <Pencil size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(plan)}
                    className="flex-1 rounded bg-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-300"
                  >
                    {plan.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => handleDelete(plan._id)}
                    className="flex flex-1 items-center justify-center gap-1 rounded bg-red-600 px-3 py-2 text-sm text-white transition hover:bg-red-700"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowModal(false)}
          />
          <div className="relative mx-auto flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-white shadow-xl">
            <div className="flex flex-shrink-0 items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="mb-0 text-xl font-bold text-gray-800">
                {editingPlan ? "Edit Plan" : "Add New Plan"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 transition-colors hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form id="plan-form" onSubmit={handleSubmit} className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g. 1 Month, 6 Month, 1 Year"
                    required
                    className="w-full rounded-md border border-gray-300 p-2.5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Duration (months) *
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.durationMonths}
                    onChange={(e) =>
                      setFormData({ ...formData, durationMonths: e.target.value })
                    }
                    required
                    className="w-full rounded-md border border-gray-300 p-2.5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Locations allowed *
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.maxLocations}
                    onChange={(e) =>
                      setFormData({ ...formData, maxLocations: e.target.value })
                    }
                    required
                    className="w-full rounded-md border border-gray-300 p-2.5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 mb-0 text-xs text-gray-500">
                    How many locations an admin on this plan can add. Free trial allows 1.
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Price ({activeCurrencyCode()}) *
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    placeholder="e.g. 29.99"
                    required
                    className="w-full rounded-md border border-gray-300 p-2.5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </form>
            </div>

            <div className="flex flex-shrink-0 justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-md bg-gray-200 px-3 py-2 text-gray-700 transition hover:bg-gray-300 sm:px-5"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="plan-form"
                disabled={formLoading}
                className="rounded-md bg-blue-600 px-3 py-2 text-white transition hover:bg-blue-700 sm:px-5"
              >
                {formLoading
                  ? editingPlan
                    ? "Saving..."
                    : "Creating..."
                  : editingPlan
                    ? "Save Changes"
                    : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansManagement;
